import { useEffect, useState, useCallback, useRef } from "react";
import api, { errMsg } from "../lib/api";
import { fmtIDR, fmtNum, fmtDate, today } from "../lib/format";
import { PageHeader, Card, Badge, Modal, Field, Btn, DataTable, inputCls, statusTone } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2, Check, X, MapPin, FileSignature, Camera } from "lucide-react";
import { toast } from "sonner";
import MapPicker from "../components/MapPicker";

const emptyPForm = { name: "", location_id: "", unit_ids: [], verifier_ids: [], start_date: today(), end_date: "", progress: 0, status: "berjalan", rab_items: [], code: "", branch: "" };
const emptyLocForm = { name: "", code: "", address: "", geofence_lat: null, geofence_lng: null, geofence_radius_m: 200, manager_id: "", mandor_id: "" };

function ProgressPhoto({ id }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let obj;
    api.get(`/progress/${id}/photo`, { responseType: "blob" }).then((r) => { obj = URL.createObjectURL(r.data); setUrl(obj); }).catch(() => {});
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [id]);
  return url
    ? <img src={url} alt="foto progress" className="w-full h-28 object-cover rounded-lg border border-slate-800" data-testid={`progress-photo-${id}`} />
    : <div className="w-full h-28 rounded-lg border border-slate-800 bg-slate-950 flex items-center justify-center text-[10px] text-slate-600">memuat foto...</div>;
}

export default function Proyek() {
  const { hasPerm, user } = useAuth();
  const isMandor = user?.role === "Mandor";
  const [tab, setTab] = useState("konstruksi");
  const [projects, setProjects] = useState([]);
  const [mrs, setMrs] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [units, setUnits] = useState([]);
  const [showProject, setShowProject] = useState(false);
  const [showMR, setShowMR] = useState(false);
  const [showLoc, setShowLoc] = useState(false);
  const [showBlock, setShowBlock] = useState(null);
  const [detail, setDetail] = useState(null);
  const [pForm, setPForm] = useState(emptyPForm);
  const [locForm, setLocForm] = useState(emptyLocForm);
  const [blockName, setBlockName] = useState("");
  const [mrForm, setMrForm] = useState({ project_id: "", warehouse_id: "", date: today(), notes: "", items: [{ item_id: "", qty: 0 }] });
  const [editId, setEditId] = useState(null);
  const [users, setUsers] = useState([]);
  const [pendingProg, setPendingProg] = useState([]);
  const [progViewId, setProgViewId] = useState("");
  const [progressList, setProgressList] = useState([]);
  const [progForm, setProgForm] = useState({ project_id: "", percent: 0, description: "" });
  const [progPhoto, setProgPhoto] = useState(null);
  const progFileRef = useRef(null);
  const [locBlocks, setLocBlocks] = useState({});
  const [editBlock, setEditBlock] = useState(null);
  const [plans, setPlans] = useState([]);
  const [planForm, setPlanForm] = useState({ plan_date: today(), title: "", note: "" });
  const [viewPlans, setViewPlans] = useState([]);

  const load = useCallback(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/projects/material-requests").then((r) => setMrs(r.data)).catch(() => {});
    api.get("/inventory/items").then((r) => setItems(r.data)).catch(() => {});
    api.get("/inventory/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    api.get("/sales/units").then((r) => setUnits(r.data)).catch(() => {});
    api.get("/progress/pending").then((r) => setPendingProg(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const wrap = (fn, okMsg, closer) => async () => {
    try { await fn(); toast.success(okMsg); closer && closer(); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const saveProgress = async (p, progress) => {
    try {
      await api.put(`/projects/${p.id}`, { name: p.name, location: p.location || "", start_date: p.start_date, end_date: p.end_date, progress: +progress, status: p.status, rab_items: p.rab_items || [], code: p.code || "", branch: p.branch || "", location_id: p.location_id || null, unit_ids: p.unit_ids || [], verifier_ids: p.verifier_ids || [] });
      toast.success("Progres diperbarui");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleUnit = (uid) => {
    const cur = pForm.unit_ids;
    if (cur.includes(uid)) setPForm({ ...pForm, unit_ids: cur.filter((x) => x !== uid) });
    else if (cur.length >= 2) toast.error("1 Proyek maksimal 2 Unit Rumah");
    else setPForm({ ...pForm, unit_ids: [...cur, uid] });
  };

  const openEditProject = (p) => {
    setPForm({ name: p.name, location_id: p.location_id || "", unit_ids: p.unit_ids || [], verifier_ids: p.verifier_ids || [],
      start_date: p.start_date || "", end_date: p.end_date || "", progress: p.progress || 0, status: p.status || "berjalan",
      rab_items: p.rab_items || [], code: p.code || "", branch: p.branch || "" });
    setEditId(p.id);
    setShowProject(true);
  };

  const saveProject = async () => {
    if (editId) await api.put(`/projects/${editId}`, { ...pForm, location_id: pForm.location_id || null });
    else await api.post("/projects", { ...pForm, location_id: pForm.location_id || null });
  };

  const deleteProject = async (p) => {
    if (!window.confirm(`Hapus proyek "${p.name}"? Riwayat SPK & progress tersimpan sebagai arsip.`)) return;
    try { await api.delete(`/projects/${p.id}`); toast.success("Proyek dihapus"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteLocation = async (l) => {
    if (!window.confirm(`Hapus lokasi "${l.name}"? Lokasi yang masih memiliki unit tidak dapat dihapus.`)) return;
    try { await api.delete(`/locations/${l.id}`); toast.success("Lokasi dihapus"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleBlocks = async (l) => {
    if (locBlocks[l.id]) { setLocBlocks({ ...locBlocks, [l.id]: null }); return; }
    try { const r = await api.get(`/locations/${l.id}/blocks`); setLocBlocks({ ...locBlocks, [l.id]: r.data }); } catch (e) { toast.error(errMsg(e)); }
  };

  const saveBlockRename = async () => {
    try {
      await api.put(`/blocks/${editBlock.id}`, { name: editBlock.name });
      toast.success("Blok diperbarui");
      const lid = editBlock.location_id;
      setEditBlock(null);
      const r = await api.get(`/locations/${lid}/blocks`);
      setLocBlocks((prev) => ({ ...prev, [lid]: r.data }));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteBlock = async (b) => {
    if (!window.confirm(`Hapus Blok ${b.name}? Blok yang masih memiliki unit tidak dapat dihapus.`)) return;
    try {
      await api.delete(`/blocks/${b.id}`);
      toast.success("Blok dihapus");
      const r = await api.get(`/locations/${b.location_id}/blocks`);
      setLocBlocks((prev) => ({ ...prev, [b.location_id]: r.data }));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const onProgPhoto = (e) => {
    const f = e.target.files[0];
    if (f) setProgPhoto(f);
  };

  const loadProgress = useCallback((pid) => {
    if (!pid) { setProgressList([]); setViewPlans([]); return; }
    api.get(`/projects/${pid}/progress`).then((r) => setProgressList(r.data)).catch(() => {});
    api.get(`/projects/${pid}/work-plans`).then((r) => setViewPlans(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (detail?.id) api.get(`/projects/${detail.id}/work-plans`).then((r) => setPlans(r.data)).catch(() => setPlans([]));
  }, [detail?.id]);

  const addPlan = async () => {
    if (!planForm.title.trim()) return toast.error("Judul rencana wajib diisi");
    try {
      await api.post(`/projects/${detail.id}/work-plans`, planForm);
      toast.success("Rencana ditambahkan — tampil di kalender Mandor & Manajer Proyek");
      setPlanForm({ plan_date: today(), title: "", note: "" });
      const r = await api.get(`/projects/${detail.id}/work-plans`);
      setPlans(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deletePlan = async (wid) => {
    try {
      await api.delete(`/work-plans/${wid}`);
      toast.success("Rencana dihapus");
      setPlans(plans.filter((w) => w.id !== wid));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submitProgress = async () => {
    if (!progForm.project_id) return toast.error("Pilih proyek konstruksi");
    if (!progForm.description.trim()) return toast.error("Deskripsi pekerjaan hari ini wajib diisi");
    if (!progPhoto) return toast.error("Foto progress wajib diunggah");
    const fd = new FormData();
    fd.append("percent", progForm.percent);
    fd.append("description", progForm.description);
    fd.append("photo", progPhoto);
    try {
      await api.post(`/projects/${progForm.project_id}/progress`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Progress terkirim — menunggu verifikasi staff verifikator");
      if (progViewId === progForm.project_id) loadProgress(progViewId);
      setProgForm({ ...progForm, percent: 0, description: "" });
      setProgPhoto(null);
      if (progFileRef.current) progFileRef.current.value = "";
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const verifyProgress = async (id, action) => {
    const note = action === "reject" ? (window.prompt("Alasan penolakan (opsional):") || "") : "";
    try {
      await api.put(`/progress/${id}/verify`, { action, note });
      toast.success(action === "verify" ? "Progress terverifikasi — progres proyek diperbarui" : "Progress ditolak");
      if (progViewId) loadProgress(progViewId);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const candidateUnits = units.filter((u) => u.location_id === pForm.location_id && (["tersedia", "dibangun"].includes(u.status) || pForm.unit_ids.includes(u.id)));
  const locName = (id) => locations.find((l) => l.id === id)?.name || "-";
  const userName = (id) => users.find((u) => u.id === id)?.name || null;
  const unitLabel = (id) => { const u = units.find((x) => x.id === id); return u ? `${u.block}-${u.number}` : "?"; };

  return (
    <div data-testid="proyek-page">
      <PageHeader title="Proyek" subtitle="1 Proyek konstruksi = pelaksanaan pembangunan fisik maksimal 2 Unit Rumah" testid="proyek-header">
        {tab === "konstruksi" && hasPerm("proyek", "create") && (
          <>
            <Btn tone="ghost" onClick={() => { setPForm(emptyPForm); setEditId(null); setShowProject(true); }} data-testid="add-project-button"><Plus className="w-4 h-4" /> Proyek Konstruksi</Btn>
            <Btn onClick={() => { setMrForm({ project_id: projects[0]?.id || "", warehouse_id: warehouses[0]?.id || "", date: today(), notes: "", items: [{ item_id: "", qty: 0 }] }); setShowMR(true); }} data-testid="add-mr-button"><Plus className="w-4 h-4" /> Request Material</Btn>
          </>
        )}
        {tab === "lokasi" && hasPerm("proyek", "create") && (
          <Btn onClick={() => { setLocForm(emptyLocForm); setShowLoc(true); }} data-testid="add-location-button"><Plus className="w-4 h-4" /> Lokasi Perumahan</Btn>
        )}
      </PageHeader>

      <div className="flex gap-2 mb-5 flex-wrap">
        {[["konstruksi", "Proyek Konstruksi"], ["progress", "Progress Harian"], ["lokasi", "Lokasi Perumahan & Blok"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`proyek-tab-${k}`}
            className={`text-sm px-4 py-2 rounded-lg border transition-colors ${tab === k ? "bg-emerald-600/10 text-emerald-300 border-emerald-500/50" : "text-slate-400 border-slate-800 hover:bg-slate-900 hover:text-slate-200"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "konstruksi" && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
            {projects.map((p) => {
              const variance = (p.rab_total || 0) - (p.realization || 0);
              const pct = p.rab_total > 0 ? Math.min((p.realization / p.rab_total) * 100, 100) : 0;
              return (
                <Card key={p.id} testid={`project-card-${p.id}`} className="hover:border-slate-700/80 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-base font-medium text-slate-100 cursor-pointer hover:text-emerald-300" onClick={() => setDetail(p)} data-testid={`project-name-${p.id}`}>{p.name}</h3>
                      <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" />{locName(p.location_id)} &middot; {fmtDate(p.start_date)} — {fmtDate(p.end_date)}</p>
                      <div className="flex gap-1.5 mt-2 flex-wrap">
                        {(p.unit_ids || []).map((uid) => <Badge key={uid} tone="cyan">Unit {unitLabel(uid)}</Badge>)}
                        {(p.unit_ids || []).length === 0 && <span className="text-[11px] text-slate-600">Belum ada unit terikat</span>}
                        {p.spk_approval_id && <Badge tone="amber"><FileSignature className="w-2.5 h-2.5 mr-1" />SPK beredar</Badge>}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <Badge tone={statusTone(p.status)}>{p.status}</Badge>
                      <div className="flex gap-1.5">
                        {hasPerm("proyek", "edit") && <Btn tone="amber" className="!px-2 !py-0.5 !text-[10px]" onClick={() => openEditProject(p)} data-testid={`project-edit-${p.id}`}>Edit</Btn>}
                        {hasPerm("proyek", "delete") && <Btn tone="danger" className="!px-2 !py-0.5 !text-[10px]" onClick={() => deleteProject(p)} data-testid={`project-delete-${p.id}`}>Hapus</Btn>}
                      </div>
                    </div>
                  </div>
                  <div className="mb-4">
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-slate-400">Progres Fisik</span>
                      <span className="font-mono text-emerald-400">{p.progress}%</span>
                    </div>
                    <div className="h-2.5 bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${p.progress}%` }} />
                    </div>
                    {hasPerm("proyek", "edit") && user?.role === "Super Admin" && (
                      <input type="range" min="0" max="100" value={p.progress} onChange={(e) => saveProgress(p, e.target.value)}
                        className="w-full mt-2 accent-emerald-500" data-testid={`progress-slider-${p.id}`} />
                    )}
                    {user?.role !== "Super Admin" && (
                      <p className="text-[10px] text-slate-500 mt-2" data-testid={`progress-locked-${p.id}`}>Persentase dikendalikan oleh laporan progress harian terverifikasi</p>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-slate-950 border border-slate-800 rounded-lg p-2.5">
                      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">RAB</div>
                      <div className="font-mono text-xs text-slate-200 mt-1">{fmtIDR(p.rab_total)}</div>
                    </div>
                    <div className="bg-slate-950 border border-slate-800 rounded-lg p-2.5">
                      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Realisasi</div>
                      <div className="font-mono text-xs text-amber-400 mt-1">{fmtIDR(p.realization)}</div>
                    </div>
                    <div className="bg-slate-950 border border-slate-800 rounded-lg p-2.5">
                      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Selisih</div>
                      <div className={`font-mono text-xs mt-1 ${variance >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{fmtIDR(variance)}</div>
                    </div>
                  </div>
                  <div className="mt-2">
                    <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${pct > 90 ? "bg-rose-500" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1 text-right">Serapan anggaran: {pct.toFixed(1)}%</div>
                  </div>
                </Card>
              );
            })}
            {projects.length === 0 && <Card><p className="text-slate-500 text-sm">Belum ada proyek konstruksi. Buat proyek dan ikat maksimal 2 unit rumah.</p></Card>}
          </div>

          <h2 className="text-lg font-medium text-slate-200 mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Request Material</h2>
          <DataTable testid="mr-table" searchKeys={["number", "project_name", "requested_by"]}
            columns={[
              { header: "No. MR", accessor: "number", mono: true },
              { header: "Proyek", accessor: "project_name" },
              { header: "Tanggal", render: (r) => fmtDate(r.date) },
              { header: "Material", render: (r) => <span className="text-xs text-slate-400">{r.items.map((i) => `${i.item_name} (${fmtNum(i.qty)} ${i.unit})`).join(", ")}</span> },
              { header: "Estimasi Biaya", render: (r) => <span className="font-mono text-xs text-amber-400">{fmtIDR(r.total_cost)}</span> },
              { header: "Status", render: (r) => <Badge tone={statusTone(r.status)}>{r.status}</Badge> },
              { header: "Aksi", render: (r) => r.status === "pending" && hasPerm("proyek", "approve") ? (
                <div className="flex gap-1.5">
                  <Btn className="!px-2.5 !py-1 !text-xs" onClick={wrap(() => api.put(`/projects/material-requests/${r.id}/approve`), "MR disetujui, stok terpotong & terjurnal ke cost center")} data-testid={`mr-approve-${r.number}`}><Check className="w-3 h-3" /> Setujui</Btn>
                  <Btn tone="danger" className="!px-2.5 !py-1 !text-xs" onClick={wrap(() => api.put(`/projects/material-requests/${r.id}/reject`, {}), "MR ditolak")} data-testid={`mr-reject-${r.number}`}><X className="w-3 h-3" /></Btn>
                </div>
              ) : null },
            ]}
            rows={mrs} empty="Belum ada request material" />
        </>
      )}

      {tab === "progress" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="progress-tab">
          <div className="space-y-4">
            {(isMandor || hasPerm("proyek", "create")) && (
              <Card testid="progress-form-card">
                <h3 className="text-sm font-medium text-slate-200 mb-3">Submit Progress Hari Ini {isMandor && <span className="text-[10px] text-amber-400 font-mono ml-1">wajib sebelum absen pulang</span>}</h3>
                <div className="space-y-3">
                  <Field label="Proyek Konstruksi">
                    <select className={inputCls} value={progForm.project_id} onChange={(e) => setProgForm({ ...progForm, project_id: e.target.value })} data-testid="progress-project-select">
                      <option value="">Pilih proyek...</option>
                      {projects.filter((p) => p.status === "berjalan").map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </Field>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Persentase Progress (%)"><input type="number" min="0" max="100" className={inputCls} value={progForm.percent || ""} onChange={(e) => setProgForm({ ...progForm, percent: Math.min(100, Math.max(0, +e.target.value)) })} data-testid="progress-percent-input" /></Field>
                    <Field label="Foto Progress (wajib)">
                      <input ref={progFileRef} type="file" accept="image/*" className="hidden" onChange={onProgPhoto} data-testid="progress-photo-input" />
                      <button onClick={() => progFileRef.current.click()} className="w-full border border-dashed border-slate-700 hover:border-emerald-500/50 rounded-lg py-2.5 text-xs text-slate-400 hover:text-emerald-300 transition-colors flex items-center justify-center gap-2" data-testid="progress-photo-button">
                        <Camera className="w-4 h-4" /> {progPhoto ? progPhoto.name : "Pilih foto..."}
                      </button>
                    </Field>
                  </div>
                  <Field label="Pekerjaan yang dilakukan hari ini">
                    <textarea className={inputCls + " min-h-[80px]"} placeholder="Contoh: Pengecoran sloof unit A-3, pemasangan bata unit A-4..." value={progForm.description} onChange={(e) => setProgForm({ ...progForm, description: e.target.value })} data-testid="progress-desc-input" />
                  </Field>
                  <Btn className="w-full justify-center" onClick={submitProgress} data-testid="progress-submit-button">Kirim Progress (menunggu verifikasi)</Btn>
                </div>
              </Card>
            )}
            {pendingProg.length > 0 && (
              <div>
                <h3 className="text-sm font-mono uppercase tracking-wider text-amber-400 mb-2">Menunggu Verifikasi Anda ({pendingProg.length})</h3>
                <div className="space-y-3">
                  {pendingProg.map((pr) => (
                    <Card key={pr.id} className="!p-3" testid={`pending-progress-${pr.id}`}>
                      <div className="flex gap-3">
                        <div className="w-28 shrink-0"><ProgressPhoto id={pr.id} /></div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-slate-200 font-medium">{pr.project_name} <span className="font-mono text-emerald-400">{pr.percent}%</span></div>
                          <div className="text-[11px] text-slate-500">{fmtDate(pr.date)} · oleh {pr.created_by}</div>
                          <p className="text-xs text-slate-400 mt-1">{pr.description}</p>
                          <div className="flex gap-1.5 mt-2">
                            <Btn className="!px-2.5 !py-1 !text-xs" onClick={() => verifyProgress(pr.id, "verify")} data-testid={`progress-verify-${pr.id}`}><Check className="w-3 h-3" /> Verifikasi</Btn>
                            <Btn tone="danger" className="!px-2.5 !py-1 !text-xs" onClick={() => verifyProgress(pr.id, "reject")} data-testid={`progress-reject-${pr.id}`}><X className="w-3 h-3" /></Btn>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div>
            <Field label="Riwayat Progress per Proyek">
              <select className={inputCls} value={progViewId} onChange={(e) => { setProgViewId(e.target.value); loadProgress(e.target.value); }} data-testid="progress-view-select">
                <option value="">Pilih proyek...</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <div className="space-y-3 mt-3">
              {[...progressList.map((x) => ({ ...x, _kind: "progress" })), ...viewPlans.map((x) => ({ ...x, _kind: "plan" }))]
                .sort((a, b) => (b._kind === "plan" ? b.plan_date : b.date).localeCompare(a._kind === "plan" ? a.plan_date : a.date))
                .map((item) => item._kind === "plan" ? (
                  <Card key={`plan-${item.id}`} className="!p-3 !border-orange-500/30" testid={`timeline-plan-${item.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge tone="orange">RENCANA</Badge>
                      <span className="text-[11px] text-slate-500">{fmtDate(item.plan_date)}</span>
                    </div>
                    <p className="text-xs text-slate-300 mt-1">{item.title}{item.note ? ` — ${item.note}` : ""}</p>
                  </Card>
                ) : (
                <Card key={item.id} className="!p-3" testid={`progress-row-${item.id}`}>
                  <div className="flex gap-3">
                    {item.has_photo && <div className="w-28 shrink-0"><ProgressPhoto id={item.id} /></div>}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-emerald-400 text-sm">{item.percent}%</span>
                        <Badge tone={{ pending: "amber", verified: "emerald", rejected: "rose" }[item.status]}>{item.status}</Badge>
                        <span className="text-[11px] text-slate-500">{fmtDate(item.date)}</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{item.description}</p>
                      <div className="text-[10px] text-slate-600 mt-1">oleh {item.created_by}{item.verified_by ? ` · diverifikasi ${item.verified_by}` : ""}{item.rejected_by ? ` · ditolak ${item.rejected_by}${item.reject_note ? ` (${item.reject_note})` : ""}` : ""}</div>
                    </div>
                  </div>
                </Card>
              ))}
              {progViewId && progressList.length + viewPlans.length === 0 && <Card><p className="text-slate-500 text-sm">Belum ada rencana maupun update progress untuk proyek ini.</p></Card>}
              {!progViewId && <Card><p className="text-slate-500 text-sm">Pilih proyek untuk melihat rencana & riwayat progress harian.</p></Card>}
            </div>
          </div>
        </div>
      )}

      {tab === "lokasi" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="locations-grid">
          {locations.map((l) => (
            <Card key={l.id} testid={`location-card-${l.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-base font-medium text-slate-100">{l.name}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{l.address || "-"} {l.code && <span className="font-mono">({l.code})</span>}</p>
                </div>
                {l.geofence_lat != null ? <Badge tone="emerald">Geofence aktif</Badge> : <Badge tone="slate">Tanpa geofence</Badge>}
              </div>
              <div className="flex gap-2 text-xs text-slate-400 mb-3">
                <Badge tone="cyan">{l.block_count} blok</Badge>
                <Badge tone="indigo">{l.unit_count} unit</Badge>
                {l.geofence_lat != null && <span className="font-mono text-[10px] self-center">{l.geofence_lat?.toFixed(5)}, {l.geofence_lng?.toFixed(5)} · r={l.geofence_radius_m}m</span>}
              </div>
              {(l.manager_id || l.mandor_id) && (
                <div className="flex gap-1.5 flex-wrap mb-3" data-testid={`location-assignees-${l.id}`}>
                  {l.manager_id && <Badge tone="emerald">MP: {userName(l.manager_id) || "tertugas"}</Badge>}
                  {l.mandor_id && <Badge tone="cyan">Mandor: {userName(l.mandor_id) || "tertugas"}</Badge>}
                </div>
              )}
              {hasPerm("proyek", "edit") && (
                <div className="flex gap-2 flex-wrap">
                  <Btn tone="ghost" className="!text-xs" onClick={() => { setLocForm({ name: l.name, code: l.code || "", address: l.address || "", geofence_lat: l.geofence_lat, geofence_lng: l.geofence_lng, geofence_radius_m: l.geofence_radius_m || 200, manager_id: l.manager_id || "", mandor_id: l.mandor_id || "" }); setShowLoc(l); }} data-testid={`location-edit-${l.id}`}>Edit / Tagging Peta</Btn>
                  {hasPerm("proyek", "create") && <Btn tone="ghost" className="!text-xs" onClick={() => { setShowBlock(l); setBlockName(""); }} data-testid={`block-add-${l.id}`}><Plus className="w-3 h-3" /> Blok</Btn>}
                  <Btn tone="ghost" className="!text-xs" onClick={() => toggleBlocks(l)} data-testid={`block-list-toggle-${l.id}`}>{locBlocks[l.id] ? "Tutup Blok" : "Kelola Blok"}</Btn>
                  {hasPerm("proyek", "delete") && <Btn tone="danger" className="!text-xs" onClick={() => deleteLocation(l)} data-testid={`location-delete-${l.id}`}>Hapus</Btn>}
                </div>
              )}
              {locBlocks[l.id] && (
                <div className="mt-3 space-y-1.5" data-testid={`block-list-${l.id}`}>
                  {locBlocks[l.id].map((b) => (
                    <div key={b.id} className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded-lg px-3 py-1.5">
                      <span className="text-xs text-slate-200 font-mono">Blok {b.name} <span className="text-slate-500">({b.unit_count} unit)</span></span>
                      {hasPerm("proyek", "edit") && (
                        <div className="flex gap-1.5">
                          <Btn tone="amber" className="!px-2 !py-0.5 !text-[10px]" onClick={() => setEditBlock({ ...b })} data-testid={`block-rename-${b.id}`}>Rename</Btn>
                          {hasPerm("proyek", "delete") && <Btn tone="danger" className="!px-2 !py-0.5 !text-[10px]" onClick={() => deleteBlock(b)} data-testid={`block-delete-${b.id}`}>Hapus</Btn>}
                        </div>
                      )}
                    </div>
                  ))}
                  {locBlocks[l.id].length === 0 && <p className="text-[11px] text-slate-600">Belum ada blok.</p>}
                </div>
              )}
            </Card>
          ))}
          {locations.length === 0 && <Card><p className="text-slate-500 text-sm">Belum ada lokasi perumahan.</p></Card>}
        </div>
      )}

      <Modal open={showProject} onClose={() => { setShowProject(false); setEditId(null); }} title={editId ? "Edit Proyek Konstruksi" : "Proyek Konstruksi Baru (maks 2 Unit)"} wide testid="project-form-modal">
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Nama Proyek"><input className={inputCls} placeholder="Pembangunan Unit A-3 & A-4" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} data-testid="project-name-input" /></Field>
            <Field label="Lokasi Perumahan">
              <select className={inputCls} value={pForm.location_id} onChange={(e) => setPForm({ ...pForm, location_id: e.target.value, unit_ids: [] })} data-testid="project-location-select">
                <option value="">Pilih lokasi...</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </Field>
          </div>
          {pForm.location_id && (
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2">Unit Rumah (pilih maksimal 2)</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-h-40 overflow-y-auto" data-testid="project-unit-picker">
                {candidateUnits.map((u) => (
                  <button key={u.id} type="button" onClick={() => toggleUnit(u.id)} data-testid={`project-unit-${u.block}${u.number}`}
                    className={`border rounded-lg px-2 py-1.5 text-xs font-mono transition-colors ${pForm.unit_ids.includes(u.id) ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                    {u.block}-{u.number} · {u.type}
                  </button>
                ))}
                {candidateUnits.length === 0 && <p className="text-slate-500 text-xs col-span-4">Tidak ada unit tersedia di lokasi ini.</p>}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">{pForm.unit_ids.length}/2 unit dipilih — SPK Tukang akan otomatis diterbitkan & masuk workflow tanda tangan.</div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Mulai"><input type="date" className={inputCls} value={pForm.start_date} onChange={(e) => setPForm({ ...pForm, start_date: e.target.value })} data-testid="project-start-input" /></Field>
            <Field label="Selesai"><input type="date" className={inputCls} value={pForm.end_date} onChange={(e) => setPForm({ ...pForm, end_date: e.target.value })} data-testid="project-end-input" /></Field>
          </div>
          <div>
            <div className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2">Staff Verifikator Progress Harian (ditunjuk admin)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-32 overflow-y-auto" data-testid="project-verifier-picker">
              {users.filter((u) => u.role !== "Super Admin" && u.active !== false).map((u) => (
                <label key={u.id} className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer bg-slate-950 border border-slate-800 rounded-lg px-2.5 py-1.5">
                  <input type="checkbox" className="w-3.5 h-3.5 accent-emerald-500" checked={pForm.verifier_ids.includes(u.id)}
                    onChange={(e) => setPForm({ ...pForm, verifier_ids: e.target.checked ? [...pForm.verifier_ids, u.id] : pForm.verifier_ids.filter((x) => x !== u.id) })}
                    data-testid={`verifier-check-${u.id}`} />
                  {u.name} <span className="text-slate-500">({u.role})</span>
                </label>
              ))}
              {users.length === 0 && <p className="text-slate-600 text-xs col-span-2">Daftar pengguna hanya dapat dimuat oleh admin (Pengaturan).</p>}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Staff terpilih memverifikasi update progress harian mandor. Absen pulang mandor baru bisa dilakukan setelah progress hari ini TERVERIFIKASI.</p>
          </div>
          <div>
            <div className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2">Item RAB</div>
            {pForm.rab_items.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_180px_32px] gap-2 items-center mb-2">
                <input className={inputCls} placeholder="Nama pekerjaan" value={r.name} onChange={(e) => setPForm({ ...pForm, rab_items: pForm.rab_items.map((x, xi) => xi === i ? { ...x, name: e.target.value } : x) })} data-testid={`rab-name-${i}`} />
                <input type="number" className={inputCls} placeholder="Anggaran (Rp)" value={r.budget || ""} onChange={(e) => setPForm({ ...pForm, rab_items: pForm.rab_items.map((x, xi) => xi === i ? { ...x, budget: +e.target.value } : x) })} data-testid={`rab-budget-${i}`} />
                <button className="text-slate-600 hover:text-rose-400" onClick={() => setPForm({ ...pForm, rab_items: pForm.rab_items.filter((_, xi) => xi !== i) })}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <Btn tone="ghost" className="!text-xs" onClick={() => setPForm({ ...pForm, rab_items: [...pForm.rab_items, { name: "", budget: 0 }] })} data-testid="rab-add-button"><Plus className="w-3.5 h-3.5" /> Item RAB</Btn>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Btn tone="ghost" onClick={() => setShowProject(false)}>Batal</Btn>
            <Btn onClick={wrap(saveProject, editId ? "Proyek diperbarui" : "Proyek dibuat & SPK diterbitkan", () => { setShowProject(false); setEditId(null); })} data-testid="project-save-button">{editId ? "Simpan Perubahan" : "Simpan Proyek"}</Btn>
          </div>
        </div>
      </Modal>

      <Modal open={!!showLoc} onClose={() => setShowLoc(false)} title={showLoc?.id ? `Edit Lokasi: ${showLoc.name}` : "Lokasi Perumahan Baru"} wide testid="location-form-modal">
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Nama Lokasi"><input className={inputCls} value={locForm.name} onChange={(e) => setLocForm({ ...locForm, name: e.target.value })} data-testid="location-name-input" /></Field>
            <Field label="Kode"><input className={inputCls} placeholder="EMR-01" value={locForm.code} onChange={(e) => setLocForm({ ...locForm, code: e.target.value })} data-testid="location-code-input" /></Field>
            <Field label="Radius Geofence (m)"><input type="number" className={inputCls} value={locForm.geofence_radius_m} onChange={(e) => setLocForm({ ...locForm, geofence_radius_m: +e.target.value })} data-testid="location-radius-input" /></Field>
          </div>
          <Field label="Alamat"><input className={inputCls} value={locForm.address} onChange={(e) => setLocForm({ ...locForm, address: e.target.value })} data-testid="location-address-input" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Penanggung Jawab — Manajer Proyek">
              <select className={inputCls} value={locForm.manager_id} onChange={(e) => setLocForm({ ...locForm, manager_id: e.target.value })} data-testid="location-manager-select">
                <option value="">Tidak ada...</option>
                {users.filter((u) => u.role === "Manajer Proyek").map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
            <Field label="Penanggung Jawab — Mandor">
              <select className={inputCls} value={locForm.mandor_id} onChange={(e) => setLocForm({ ...locForm, mandor_id: e.target.value })} data-testid="location-mandor-select">
                <option value="">Tidak ada...</option>
                {users.filter((u) => u.role === "Mandor").map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
          </div>
          <p className="text-[11px] text-amber-400/80" data-testid="location-assign-hint">Penugasan otomatis: akses kedua user dibatasi HANYA ke lokasi ini (semua data: proyek, progress, absensi, material, unit), dan Manajer Proyek otomatis menjadi verifikator progress semua proyek di lokasi ini. Super Admin tetap bisa verifikasi sebagai cadangan.</p>
          <div>
            <span className="text-xs font-mono uppercase tracking-wider text-slate-400 block mb-1.5">Zona Geofence Absensi — klik peta untuk tagging koordinat</span>
            <MapPicker lat={locForm.geofence_lat} lng={locForm.geofence_lng} radius={locForm.geofence_radius_m} onPick={(la, ln) => setLocForm({ ...locForm, geofence_lat: la, geofence_lng: ln })} testid="location-geofence-map" />
            {locForm.geofence_lat != null && <div className="text-xs font-mono text-emerald-400 mt-1.5" data-testid="location-geofence-tagged">Tertagih: {locForm.geofence_lat.toFixed(6)}, {locForm.geofence_lng.toFixed(6)} (radius {locForm.geofence_radius_m} m)</div>}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Btn tone="ghost" onClick={() => setShowLoc(false)}>Batal</Btn>
            <Btn onClick={wrap(() => showLoc?.id ? api.put(`/locations/${showLoc.id}`, locForm) : api.post("/locations", locForm), "Lokasi disimpan", () => setShowLoc(false))} data-testid="location-save-button">Simpan Lokasi</Btn>
          </div>
        </div>
      </Modal>

      <Modal open={!!showBlock} onClose={() => setShowBlock(null)} title={showBlock ? `Tambah Blok — ${showBlock.name}` : ""} testid="block-form-modal">
        <div className="space-y-3">
          <Field label="Nama Blok"><input className={inputCls} placeholder="D" value={blockName} onChange={(e) => setBlockName(e.target.value.toUpperCase())} data-testid="block-name-input" /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <Btn tone="ghost" onClick={() => setShowBlock(null)}>Batal</Btn>
            <Btn onClick={wrap(() => api.post("/blocks", { location_id: showBlock.id, name: blockName }), "Blok ditambahkan", () => setShowBlock(null))} data-testid="block-save-button">Simpan Blok</Btn>
          </div>
        </div>
      </Modal>

      <Modal open={!!editBlock} onClose={() => setEditBlock(null)} title={editBlock ? `Rename Blok ${editBlock.name}` : ""} testid="block-edit-modal">
        {editBlock && (
          <div className="space-y-3">
            <Field label="Nama Blok Baru"><input className={inputCls} value={editBlock.name} onChange={(e) => setEditBlock({ ...editBlock, name: e.target.value.toUpperCase() })} data-testid="block-rename-input" /></Field>
            <p className="text-[11px] text-slate-500">Label blok pada semua unit terkait ikut diperbarui otomatis.</p>
            <div className="flex justify-end gap-2 pt-2">
              <Btn tone="ghost" onClick={() => setEditBlock(null)}>Batal</Btn>
              <Btn onClick={saveBlockRename} data-testid="block-rename-save">Simpan</Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={showMR} onClose={() => setShowMR(false)} title="Request Material ke Gudang" wide testid="mr-form-modal">
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Proyek Konstruksi">
              <select className={inputCls} value={mrForm.project_id} onChange={(e) => setMrForm({ ...mrForm, project_id: e.target.value })} data-testid="mr-project-select">
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Dari Gudang">
              <select className={inputCls} value={mrForm.warehouse_id} onChange={(e) => setMrForm({ ...mrForm, warehouse_id: e.target.value })} data-testid="mr-warehouse-select">
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </Field>
            <Field label="Tanggal"><input type="date" className={inputCls} value={mrForm.date} onChange={(e) => setMrForm({ ...mrForm, date: e.target.value })} data-testid="mr-date-input" /></Field>
          </div>
          {mrForm.items.map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_100px_32px] gap-2 items-center">
              <select className={inputCls} value={l.item_id} onChange={(e) => setMrForm({ ...mrForm, items: mrForm.items.map((x, xi) => xi === i ? { ...x, item_id: e.target.value } : x) })} data-testid={`mr-item-${i}`}>
                <option value="">Pilih material...</option>
                {items.map((it) => <option key={it.id} value={it.id}>{it.code} - {it.name}</option>)}
              </select>
              <input type="number" placeholder="Qty" className={inputCls} value={l.qty || ""} onChange={(e) => setMrForm({ ...mrForm, items: mrForm.items.map((x, xi) => xi === i ? { ...x, qty: +e.target.value } : x) })} data-testid={`mr-qty-${i}`} />
              <button className="text-slate-600 hover:text-rose-400" onClick={() => setMrForm({ ...mrForm, items: mrForm.items.filter((_, xi) => xi !== i) })}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <Btn tone="ghost" className="!text-xs" onClick={() => setMrForm({ ...mrForm, items: [...mrForm.items, { item_id: "", qty: 0 }] })} data-testid="mr-add-line"><Plus className="w-3.5 h-3.5" /> Baris</Btn>
          <p className="text-[11px] text-slate-500">Saat disetujui: stok gudang terpotong + jurnal beban material ke cost center lokasi proyek.</p>
          <div className="flex justify-end gap-2 pt-2">
            <Btn tone="ghost" onClick={() => setShowMR(false)}>Batal</Btn>
            <Btn onClick={wrap(() => api.post("/projects/material-requests", { ...mrForm, items: mrForm.items.filter((i) => i.item_id && i.qty > 0) }), "Request material dikirim", () => setShowMR(false))} data-testid="mr-save-button">Kirim Request</Btn>
          </div>
        </div>
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail?.name || ""} testid="project-detail-modal">
        {detail && (
          <div className="space-y-2">
            <div className="text-xs text-slate-500">Lokasi: <span className="text-slate-300">{locName(detail.location_id)}</span> · Unit: <span className="font-mono text-cyan-300">{(detail.unit_ids || []).map(unitLabel).join(", ") || "-"}</span></div>
            <div className="text-xs font-mono uppercase tracking-wider text-slate-400 mt-2">Rincian RAB</div>
            {(detail.rab_items || []).map((r, i) => (
              <div key={i} className="flex justify-between text-sm border-b border-slate-800/60 py-1.5">
                <span className="text-slate-300">{r.name}</span>
                <span className="font-mono text-xs text-slate-200">{fmtIDR(r.budget)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-2 text-sm font-medium">
              <span className="text-emerald-400">Total RAB</span>
              <span className="font-mono text-emerald-400">{fmtIDR(detail.rab_total)}</span>
            </div>
            <div className="border-t border-slate-800 pt-3 mt-2">
              <div className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2">Rencana Pembangunan — tampil di Kalender Mandor & Manajer Proyek</div>
              <div className="space-y-1.5 mb-3 max-h-36 overflow-y-auto" data-testid="plan-list">
                {plans.map((w) => (
                  <div key={w.id} className="flex items-center gap-2 bg-slate-950 border border-slate-800 rounded-lg px-3 py-1.5" data-testid={`plan-row-${w.id}`}>
                    <span className="font-mono text-[11px] text-orange-300 shrink-0">{fmtDate(w.plan_date)}</span>
                    <span className="text-xs text-slate-200 flex-1">{w.title}{w.note ? ` — ${w.note}` : ""}</span>
                    {hasPerm("proyek", "delete") && <button className="text-slate-600 hover:text-rose-400" onClick={() => deletePlan(w.id)} data-testid={`plan-delete-${w.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div>
                ))}
                {plans.length === 0 && <p className="text-[11px] text-slate-600">Belum ada rencana pembangunan.</p>}
              </div>
              {hasPerm("proyek", "create") && (
                <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr_1fr_auto] gap-2 items-end">
                  <input type="date" className={inputCls} value={planForm.plan_date} onChange={(e) => setPlanForm({ ...planForm, plan_date: e.target.value })} data-testid="plan-date-input" />
                  <input className={inputCls} placeholder="Rencana kerja (mis: Pengecoran sloof)" value={planForm.title} onChange={(e) => setPlanForm({ ...planForm, title: e.target.value })} data-testid="plan-title-input" />
                  <input className={inputCls} placeholder="Catatan (opsional)" value={planForm.note} onChange={(e) => setPlanForm({ ...planForm, note: e.target.value })} data-testid="plan-note-input" />
                  <Btn className="!text-xs" onClick={addPlan} data-testid="plan-add-button"><Plus className="w-3.5 h-3.5" /> Tambah</Btn>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
